import { useState, FormEvent } from 'react'
import FormField from '../../../components/common/FormField'
import FileUpload from '../../../components/common/FileUpload'
import Button from '../../../components/common/Button'
import SettingsSection from './SettingsSection'
import { getImageUrl } from '../../../utils/helpers'
import { Settings } from '../Settings'

interface SettingsFormProps {
  initialData: Settings
  onSubmit: (data: Settings) => Promise<void>
  saving: boolean
}

function SettingsForm({ initialData, onSubmit, saving }: SettingsFormProps) {
  const [formData, setFormData] = useState<Settings>(initialData)
  const [errors, setErrors] = useState<Partial<Record<keyof Settings, string>>>({})

  const handleChange = (field: keyof Settings, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof Settings, string>> = {}

    // Required fields
    if (!formData.candidate_name.trim()) {
      newErrors.candidate_name = 'Le nom du candidat est requis'
    }
    if (!formData.responsable_publication.trim()) {
      newErrors.responsable_publication = 'Le responsable de la publication est requis'
    }
    if (!formData.contact_email.trim()) {
      newErrors.contact_email = "L'email est requis"
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email)) {
      newErrors.contact_email = "Format d'email invalide"
    }

    // URL validation for social media
    const urlFields: (keyof Settings)[] = ['social_facebook', 'social_twitter', 'social_instagram', 'social_linkedin']
    urlFields.forEach(field => {
      const value = formData[field]
      if (value && value.trim() && !isValidUrl(value)) {
        newErrors[field] = 'URL invalide'
      }
    })

    // Color validation
    if (formData.primary_color && !isValidColor(formData.primary_color)) {
      newErrors.primary_color = 'Couleur invalide (format: #RRGGBB)'
    }
    if (formData.secondary_color && !isValidColor(formData.secondary_color)) {
      newErrors.secondary_color = 'Couleur invalide (format: #RRGGBB)'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url)
      return true
    } catch {
      return false
    }
  }

  const isValidColor = (color: string): boolean => {
    return /^#[0-9A-Fa-f]{6}$/.test(color)
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!validateForm()) return

    try {
      await onSubmit(formData)
    } catch (err) {
      // Error handling is done in parent component
    }
  }

  const handleLogoUpload = (fileData: any) => {
    if (fileData.url) {
      handleChange('site_logo', fileData.url)
    }
  }

  const handleFaviconUpload = (fileData: any) => {
    if (fileData.url) {
      handleChange('site_favicon', fileData.url)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="settings-form">
      {/* Identity Section */}
      <SettingsSection
        title="Identité"
        description="Informations principales de votre campagne"
      >
        <div className="form-grid">
          <FormField
            label="Nom du candidat"
            id="candidate_name"
            value={formData.candidate_name}
            onChange={(e) => handleChange('candidate_name', e.target.value)}
            error={errors.candidate_name}
            required
          />
          <FormField
            label="Responsable de la publication"
            id="responsable_publication"
            value={formData.responsable_publication}
            onChange={(e) => handleChange('responsable_publication', e.target.value)}
            error={errors.responsable_publication}
            placeholder="Nom affiché dans les mentions légales"
            required
          />
          <FormField
            label="Slogan"
            id="slogan"
            value={formData.slogan}
            onChange={(e) => handleChange('slogan', e.target.value)}
            error={errors.slogan}
          />
        </div>
      </SettingsSection>

      {/* Contact Section */}
      <SettingsSection
        title="Contact"
        description="Email de contact"
      >
        <FormField
          label="Email"
          id="contact_email"
          type="email"
          value={formData.contact_email}
          onChange={(e) => handleChange('contact_email', e.target.value)}
          error={errors.contact_email}
          required
        />
      </SettingsSection>

      {/* Social Media Section */}
      <SettingsSection
        title="Réseaux sociaux"
        description="URLs de vos profils sur les réseaux sociaux"
      >
        <div className="form-grid">
          <FormField
            label="Facebook"
            id="social_facebook"
            type="url"
            value={formData.social_facebook}
            onChange={(e) => handleChange('social_facebook', e.target.value)}
            error={errors.social_facebook}
            placeholder="https://facebook.com/..."
          />
          <FormField
            label="Twitter"
            id="social_twitter"
            type="url"
            value={formData.social_twitter}
            onChange={(e) => handleChange('social_twitter', e.target.value)}
            error={errors.social_twitter}
            placeholder="https://twitter.com/..."
          />
          <FormField
            label="Instagram"
            id="social_instagram"
            type="url"
            value={formData.social_instagram}
            onChange={(e) => handleChange('social_instagram', e.target.value)}
            error={errors.social_instagram}
            placeholder="https://instagram.com/..."
          />
          <FormField
            label="LinkedIn"
            id="social_linkedin"
            type="url"
            value={formData.social_linkedin}
            onChange={(e) => handleChange('social_linkedin', e.target.value)}
            error={errors.social_linkedin}
            placeholder="https://linkedin.com/..."
          />
        </div>
      </SettingsSection>

      {/* Theme Section */}
      <SettingsSection
        title="Thème"
        description="Couleurs de votre site"
      >
        <div className="form-grid color-grid">
          <div className="color-field">
            <FormField
              label="Couleur primaire"
              id="primary_color"
              type="color"
              value={formData.primary_color}
              onChange={(e) => handleChange('primary_color', e.target.value)}
              error={errors.primary_color}
            />
            <div className="color-preview" style={{ backgroundColor: formData.primary_color }}></div>
          </div>
          <div className="color-field">
            <FormField
              label="Couleur secondaire"
              id="secondary_color"
              type="color"
              value={formData.secondary_color}
              onChange={(e) => handleChange('secondary_color', e.target.value)}
              error={errors.secondary_color}
            />
            <div className="color-preview" style={{ backgroundColor: formData.secondary_color }}></div>
          </div>
        </div>
      </SettingsSection>

      {/* Media Section */}
      <SettingsSection
        title="Médias"
        description="Logo et favicon de votre site"
      >
        <div className="media-uploads">
          <div className="media-upload-item">
            <label>Logo</label>
            {formData.site_logo && (
              <div className="media-preview">
                <img src={getImageUrl(formData.site_logo)} alt="Logo" />
              </div>
            )}
            <FileUpload
              onUpload={handleLogoUpload}
              accept="image/*"
              label={formData.site_logo ? 'Changer le logo' : 'Télécharger un logo'}
            />
          </div>

          <div className="media-upload-item">
            <label>Favicon</label>
            {formData.site_favicon && (
              <div className="media-preview favicon-preview">
                <img src={getImageUrl(formData.site_favicon)} alt="Favicon" />
              </div>
            )}
            <FileUpload
              onUpload={handleFaviconUpload}
              accept="image/*"
              label={formData.site_favicon ? 'Changer le favicon' : 'Télécharger un favicon'}
            />
          </div>
        </div>
      </SettingsSection>

      {/* Submit Button */}
      <div className="settings-form-actions">
        <Button type="submit" disabled={saving}>
          {saving ? 'Sauvegarde en cours...' : 'Enregistrer les paramètres'}
        </Button>
      </div>
    </form>
  )
}

export default SettingsForm
